import React, { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  Collapse,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronRight,
  IconPlus,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import AddOrgLevelEntityModal from "./AddOrgLevelEntityModal";
import EditOrgLevelEntityModal from "./EditOrgLevelEntityModal";
import EditOrgLevelMembersModal from "./EditOrgLevelMembersModal";
import "./OrganizationLevelsTab.css";

function OrgLevelItem({
  entity,
  parentEntityId = null,
  onAdd,
  onEdit,
  availableUsers,
  companyManagers = [],
  onMembersUpdated,
  expandSignal,
  canManage = true,
}) {
  const children = Array.isArray(entity.org_levels) ? entity.org_levels : [];
  const isInactive = entity.is_active === false;
  const isDefaultType = entity.org_level_type?.base_org_level_type === true;
  const memberList = Array.isArray(entity.member_list)
    ? entity.member_list
    : [];
  const memberCount = entity.member_count ?? memberList.length;
  const managerList = Array.isArray(entity.manager_list)
    ? entity.manager_list
    : [];
  const managerCount = entity.manager_count ?? managerList.length;
  const adminList = Array.isArray(entity.admin_list) ? entity.admin_list : [];
  const inheritedAdmins = Array.isArray(entity.inherited_admins)
    ? entity.inherited_admins
    : [];
  const adminCount = adminList.length + inheritedAdmins.length;
  const managerCandidates = memberList
    .filter(m => m.is_manager === true)
    .filter(m => !managerList.some(mg => mg.user_id === m.user_id));
  const adminCandidates = companyManagers
    .filter(m => !adminList.some(a => a.user_id === m.user_id))
    .filter(m => !inheritedAdmins.some(a => a.user_id === m.user_id));
  const [membersOpen, setMembersOpen] = useState(false);
  const [managersOpen, setManagersOpen] = useState(false);
  const [adminsOpen, setAdminsOpen] = useState(false);
  const [childrenOpen, setChildrenOpen] = useState(false);
  const [editMembersOpen, setEditMembersOpen] = useState(false);
  const [editManagersOpen, setEditManagersOpen] = useState(false);
  const [editAdminsOpen, setEditAdminsOpen] = useState(false);
  const hasChildren = children.length > 0;

  useEffect(() => {
    if (expandSignal > 0) {
      setChildrenOpen(true);
      setMembersOpen(true);
      setManagersOpen(true);
      setAdminsOpen(true);
    }
  }, [expandSignal]);

  const renderUserSection = ({
    label,
    list,
    inheritedList = [],
    count,
    isOpen,
    setOpen,
    onEditClick,
    showEdit = true,
  }) => (
    <Box mt="xs">
      <Group justify="space-between" align="center">
        <UnstyledButton onClick={() => setOpen(o => !o)}>
          <Group gap="xs" align="center">
            {isOpen ? (
              <IconChevronDown size={14} />
            ) : (
              <IconChevronRight size={14} />
            )}
            <Text size="sm">
              {label}: {count}
            </Text>
          </Group>
        </UnstyledButton>
        {showEdit && (
          <Button variant="subtle" size="xs" onClick={onEditClick}>
            Edit {label}
          </Button>
        )}
      </Group>
      <Collapse in={isOpen}>
        <Stack gap="xs" mt="xs" pl="md">
          {list.length === 0 && inheritedList.length === 0 ? (
            <Text size="sm" c="dimmed">
              No {label.toLowerCase()}
            </Text>
          ) : (
            <>
              {list.map((user, idx) => (
                <Group
                  key={user.id ?? user.user_id ?? idx}
                  justify="space-between"
                  align="center"
                >
                  <Group gap="sm" align="center">
                    <Avatar src={user.profile_image} size="sm" radius="xl" />
                    <Text size="sm">{user.name}</Text>
                  </Group>
                  {user.include_downline && (
                    <Text size="sm" c="green" style={{ fontStyle: "italic" }}>
                      included downline
                    </Text>
                  )}
                </Group>
              ))}
              {inheritedList.map((user, idx) => (
                <Group
                  key={`inh-${user.id ?? user.user_id ?? idx}`}
                  justify="space-between"
                  align="center"
                >
                  <Group gap="sm" align="center">
                    <Avatar src={user.profile_image} size="sm" radius="xl" />
                    <Text size="sm">{user.name}</Text>
                  </Group>
                  <Text size="xs" c="dimmed" style={{ fontStyle: "italic" }}>
                    inherited
                  </Text>
                </Group>
              ))}
            </>
          )}
        </Stack>
      </Collapse>
    </Box>
  );

  return (
    <Box
      style={{
        padding: ".75rem",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        backgroundColor: isInactive ? "#e0e0e0" : "#fafafa",
      }}
    >
      <Group justify="space-between" align="flex-start">
        <Stack gap={2}>
          <Group gap="xs" align="center">
            {hasChildren && (
              <ActionIcon
                variant="subtle"
                size="md"
                onClick={() => setChildrenOpen(o => !o)}
                aria-label={
                  childrenOpen ? "Collapse children" : "Expand children"
                }
              >
                {childrenOpen ? (
                  <IconChevronDown size={16} />
                ) : (
                  <IconChevronRight size={16} />
                )}
              </ActionIcon>
            )}
            <Text size="lg">
              {entity.org_level_type && entity.org_level_type.name && (
                <span style={{ fontStyle: "italic", fontSize: "0.85em" }}>
                  {entity.org_level_type.name}:{" "}
                </span>
              )}
              {canManage && !isInactive ? (
                <UnstyledButton
                  onClick={() => onEdit(entity, parentEntityId)}
                  style={{
                    fontWeight: isDefaultType ? 700 : 400,
                    fontSize: "inherit",
                    color: "inherit",
                  }}
                >
                  {entity.name}
                </UnstyledButton>
              ) : (
                <span style={{ fontWeight: isDefaultType ? 700 : 400 }}>
                  {entity.name}
                </span>
              )}
            </Text>
            {isInactive && (
              <Badge color="gray" variant="filled">
                Deactivated
              </Badge>
            )}
            {entity.history_id != null && (
              <Tooltip
                multiline
                withArrow
                label={
                  Array.isArray(entity.history) && entity.history.length > 0 ? (
                    <Stack gap={2}>
                      {entity.history.map((item, index) => (
                        <Text key={index} size="xs">
                          {item.name}
                          {item.org_level_type_name
                            ? ` (${item.org_level_type_name})`
                            : ""}{" "}
                          — {item.selling_year_name ?? "N/A"} · Revenue:{" "}
                          {item.revenue ?? "N/A"} · Accounts:{" "}
                          {item.number_of_accounts ?? "N/A"}
                        </Text>
                      ))}
                    </Stack>
                  ) : (
                    "No history"
                  )
                }
              >
                <Badge variant="light" style={{ cursor: "default" }}>
                  {entity.history_id}
                </Badge>
              </Tooltip>
            )}
          </Group>
          <Text size="sm" c="dimmed">
            Revenue: {entity.revenue ?? "N/A"} · Accounts:{" "}
            {entity.number_of_accounts ?? "N/A"}
          </Text>
        </Stack>
        {!isDefaultType && canManage && !isInactive && (
          <ActionIcon
            variant="subtle"
            size="md"
            onClick={() => onAdd(entity)}
            aria-label="Add child entity"
          >
            <IconPlus size={16} />
          </ActionIcon>
        )}
      </Group>

      {renderUserSection({
        label: "Admins",
        list: adminList,
        inheritedList: inheritedAdmins,
        count: adminCount,
        isOpen: adminsOpen,
        setOpen: setAdminsOpen,
        onEditClick: () => setEditAdminsOpen(true),
        showEdit: canManage,
      })}

      {isDefaultType && (
        <>
          {renderUserSection({
            label: "Managers",
            list: managerList,
            count: managerCount,
            isOpen: managersOpen,
            setOpen: setManagersOpen,
            onEditClick: () => setEditManagersOpen(true),
            showEdit: canManage,
          })}
          {renderUserSection({
            label: "Members",
            list: memberList,
            count: memberCount,
            isOpen: membersOpen,
            setOpen: setMembersOpen,
            onEditClick: () => setEditMembersOpen(true),
          })}
        </>
      )}

      {hasChildren && (
        <Collapse in={childrenOpen}>
          <Stack gap="sm" mt="sm" pl="md">
            {children.map(child => (
              <OrgLevelItem
                key={child.id}
                entity={child}
                parentEntityId={entity.id}
                onAdd={onAdd}
                onEdit={onEdit}
                availableUsers={availableUsers}
                companyManagers={companyManagers}
                onMembersUpdated={onMembersUpdated}
                expandSignal={expandSignal}
                canManage={canManage}
              />
            ))}
          </Stack>
        </Collapse>
      )}

      <EditOrgLevelMembersModal
        isOpen={editAdminsOpen}
        onClose={() => setEditAdminsOpen(false)}
        orgLevel={entity}
        currentMembers={adminList}
        availableUsers={adminCandidates}
        onMembersUpdated={onMembersUpdated}
        role="admin"
        roleLabel="Admins"
        roleLabelSingular="Admin"
      />

      {isDefaultType && (
        <>
          <EditOrgLevelMembersModal
            isOpen={editMembersOpen}
            onClose={() => setEditMembersOpen(false)}
            orgLevel={entity}
            currentMembers={memberList}
            availableUsers={availableUsers}
            onMembersUpdated={onMembersUpdated}
            role="member"
            roleLabel="Members"
            roleLabelSingular="Member"
          />
          <EditOrgLevelMembersModal
            isOpen={editManagersOpen}
            onClose={() => setEditManagersOpen(false)}
            orgLevel={entity}
            currentMembers={managerList}
            availableUsers={managerCandidates}
            onMembersUpdated={onMembersUpdated}
            role="manager"
            roleLabel="Managers"
            roleLabelSingular="Manager"
          />
        </>
      )}
    </Box>
  );
}

function OrgChartNode({ entity }) {
  const children = Array.isArray(entity.org_levels) ? entity.org_levels : [];
  const typeName = entity.org_level_type?.name;
  const isBaseType = entity.org_level_type?.base_org_level_type === true;
  const memberList = Array.isArray(entity.member_list)
    ? entity.member_list
    : [];
  const memberCount = entity.member_count ?? memberList.length;
  const managerList = Array.isArray(entity.manager_list)
    ? entity.manager_list
    : [];
  return (
    <li>
      <div className="org-chart-card">
        {typeName && <div className="org-chart-type">{typeName}</div>}
        <div className="org-chart-name">{entity.name}</div>
        <div className="org-chart-stats">
          Revenue: {entity.revenue ?? "N/A"} · Accounts:{" "}
          {entity.number_of_accounts ?? "N/A"}
        </div>
        {isBaseType && managerList.length > 0 && (
          <>
            <div className="org-chart-managers-heading">Managers</div>
            <div className="org-chart-managers">
              {managerList.map((user, idx) => (
                <div
                  key={user.id ?? user.user_id ?? idx}
                  className="org-chart-manager"
                >
                  <Avatar src={user.profile_image} size={20} radius="xl" />
                  <span>{user.name}</span>
                </div>
              ))}
            </div>
          </>
        )}
        {isBaseType && (
          <div className="org-chart-members">Members: {memberCount}</div>
        )}
      </div>
      {children.length > 0 && (
        <ul>
          {children.map(child => (
            <OrgChartNode key={child.id} entity={child} />
          ))}
        </ul>
      )}
    </li>
  );
}

function OrganizationLevelsTab() {
  const { getVerifiedJWT, user } = useAuth();
  const canManage = user?.role === "COMPANY_ADMIN";
  // const [topOrgLevelName, setTopOrgLevelName] = useState("");
  const [topOrgLevelId, setTopOrgLevelId] = useState(null);
  const [orgLevels, setOrgLevels] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [companyManagers, setCompanyManagers] = useState([]);
  const [entitiesByType, setEntitiesByType] = useState([]);
  const [entityList, setEntityList] = useState([]);
  const [orgLevelTypes, setOrgLevelTypes] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addParent, setAddParent] = useState(null);
  const [view, setView] = useState("details");
  const [expandSignal, setExpandSignal] = useState(0);
  const [editEntity, setEditEntity] = useState(null);
  const [editParentId, setEditParentId] = useState(null);
  const [sellingYears, setSellingYears] = useState([]);
  const [selectedSellingYearId, setSelectedSellingYearId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const fetchSellingYears = useCallback(async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v2/selling-year/list"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const years = Array.isArray(result) ? result : result.data || [];
        setSellingYears(years);
        const activeYear = years.find(y => y.is_active);
        const defaultYear = activeYear || years[0];
        if (defaultYear) {
          setSelectedSellingYearId(defaultYear.id);
        }
      }
    } catch (err) {}
  }, [getVerifiedJWT]);

  const fetchOrgLevelEntities = useCallback(async () => {
    if (!selectedSellingYearId) return;
    setLoading(true);
    setOrgLevels([]);
    setAvailableUsers([]);
    setCompanyManagers([]);
    setEntitiesByType([]);
    setEntityList([]);
    setOrgLevelTypes([]);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(
        getApiEndpoint(`/v1/org-level/entities/${selectedSellingYearId}`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        const data = result.data || result || {};
        // setTopOrgLevelName(data.top_org_level_name || "");
        setTopOrgLevelId(data.top_org_level_id ?? null);
        setOrgLevels(Array.isArray(data.org_levels) ? data.org_levels : []);
        setAvailableUsers(
          Array.isArray(data.available_users) ? data.available_users : []
        );
        setCompanyManagers(
          Array.isArray(data.company_managers) ? data.company_managers : []
        );
        setEntitiesByType(
          Array.isArray(data.entities_by_type) ? data.entities_by_type : []
        );
        setEntityList(Array.isArray(data.entity_list) ? data.entity_list : []);
        setOrgLevelTypes(
          Array.isArray(data.org_level_types) ? data.org_level_types : []
        );
      }
    } catch (err) {
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, [getVerifiedJWT, selectedSellingYearId]);

  useEffect(() => {
    fetchSellingYears();
  }, [fetchSellingYears]);

  useEffect(() => {
    fetchOrgLevelEntities();
  }, [fetchOrgLevelEntities]);

  const handleAddEntity = async ({ name, orgLevelTypeId }) => {
    const jwt = await getVerifiedJWT();
    if (!jwt) {
      throw new Error("Authentication failed");
    }

    const response = await fetch(getApiEndpoint("/v1/org-level/entity"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({
        name,
        org_level_type_id: orgLevelTypeId ?? topOrgLevelId,
        org_level_parent_id: addParent?.id ?? null,
        selling_year_id: selectedSellingYearId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to create entity");
    }

    await fetchOrgLevelEntities();
  };

  const handleOpenAddForEntity = entity => {
    setAddParent(entity);
    setAddModalOpen(true);
  };

  const handleEditEntity = async ({
    name,
    orgLevelParentId,
    orgLevelTypeId,
  }) => {
    const jwt = await getVerifiedJWT();
    if (!jwt) {
      throw new Error("Authentication failed");
    }

    const response = await fetch(getApiEndpoint("/v1/org-level/entity"), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({
        id: editEntity.id,
        name,
        org_level_parent_id: orgLevelParentId,
        selling_year_id: selectedSellingYearId,
        ...(orgLevelTypeId != null && { org_level_type_id: orgLevelTypeId }),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to update entity");
    }

    await fetchOrgLevelEntities();
  };

  const handleDeactivateEntity = async () => {
    const jwt = await getVerifiedJWT();
    if (!jwt) {
      throw new Error("Authentication failed");
    }

    const response = await fetch(
      getApiEndpoint(
        `/v1/org-level/entity/deactivate/${editEntity.id}/${selectedSellingYearId}`
      ),
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to deactivate entity");
    }

    await fetchOrgLevelEntities();
  };

  return (
    <div className="org-page-tab-content">
      <Group justify="space-between" align="center" mb="md">
        <SegmentedControl
          value={view}
          onChange={setView}
          data={[
            { label: "Details", value: "details" },
            { label: "Hierarchy", value: "hierarchy" },
          ]}
        />
        <Group gap="xs" align="center">
          <Text size="sm" fw={500}>
            Selling Year
          </Text>
          <Select
            value={selectedSellingYearId ? String(selectedSellingYearId) : null}
            onChange={value => {
              if (!value) return;
              const match = sellingYears.find(y => String(y.id) === value);
              setSelectedSellingYearId(match ? match.id : null);
            }}
            data={sellingYears.map(y => ({
              value: String(y.id),
              label: y.name,
            }))}
            placeholder="Select selling year"
            allowDeselect={false}
            searchable={false}
            w={200}
          />
        </Group>
      </Group>

      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : (
        <>
          {view === "details" && (
            <>
              <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} mb="md">
                {entitiesByType.map(t => (
                  <Paper
                    key={t.org_level_type_id}
                    p="sm"
                    withBorder
                    radius="md"
                  >
                    <Text size="xs" c="dimmed">
                      {t.org_level_type_name}
                    </Text>
                    <Text size="xl" fw={700}>
                      {Array.isArray(t.entities) ? t.entities.length : 0}
                    </Text>
                  </Paper>
                ))}
                <Paper p="sm" withBorder radius="md">
                  <Text size="xs" c="dimmed">
                    Unassigned Users
                  </Text>
                  <Text size="xl" fw={700}>
                    {availableUsers.length}
                  </Text>
                </Paper>
              </SimpleGrid>

              <Group justify="space-between" align="center" mb="xs">
                <h2>Sales Groups</h2>
                {orgLevels.length > 0 && (
                  <Button
                    variant="subtle"
                    size="sm"
                    onClick={() => setExpandSignal(s => s + 1)}
                  >
                    Expand all
                  </Button>
                )}
              </Group>

              <Stack gap={2} mb="md">
                <Text size="xs" c="dimmed">
                  * Managers and Admins are able to view group details and
                  update members.
                </Text>
                <Text size="xs" c="dimmed">
                  * Managers must be a member of the group.
                </Text>
                <Text size="xs" c="dimmed">
                  * A user may be an Admin on multiple groups.
                </Text>
                <Text size="xs" c="dimmed">
                  * In order to be a Manager or Admin of a group a user must
                  have a role of Manager. You can update this on the user's
                  details page.
                </Text>
              </Stack>

              <Stack gap="sm">
                {orgLevels.length === 0 ? (
                  hasLoaded ? (
                    <Text c="dimmed">
                      We couldn't find any company data. Please contact customer
                      support.
                    </Text>
                  ) : null
                ) : (
                  orgLevels.map(entity => (
                    <OrgLevelItem
                      key={entity.id}
                      entity={entity}
                      onAdd={handleOpenAddForEntity}
                      onEdit={(ent, parentId) => {
                        setEditEntity(ent);
                        setEditParentId(parentId);
                      }}
                      availableUsers={availableUsers}
                      companyManagers={companyManagers}
                      onMembersUpdated={fetchOrgLevelEntities}
                      expandSignal={expandSignal}
                      canManage={canManage}
                    />
                  ))
                )}
              </Stack>
            </>
          )}

          {view === "hierarchy" && (
            <div className="org-chart">
              {orgLevels.length === 0 ? (
                hasLoaded ? (
                  <Text c="dimmed">
                    We couldn't find any company data. Please contact customer
                    support.
                  </Text>
                ) : null
              ) : (
                <ul>
                  {orgLevels.map(entity => (
                    <OrgChartNode key={entity.id} entity={entity} />
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}

      <AddOrgLevelEntityModal
        opened={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleAddEntity}
        orgLevelTypes={
          Array.isArray(addParent?.child_level_types)
            ? addParent.child_level_types
            : []
        }
      />

      <EditOrgLevelEntityModal
        opened={!!editEntity}
        onClose={() => {
          setEditEntity(null);
          setEditParentId(null);
        }}
        onSave={handleEditEntity}
        onDeactivate={handleDeactivateEntity}
        entity={editEntity}
        currentParentId={editParentId}
        entityList={entityList}
        orgLevelTypes={orgLevelTypes}
      />
    </div>
  );
}

export default OrganizationLevelsTab;
